/**
 * <combo-upsell>
 *
 * Adds the bundle product to the cart via AJAX (@theme/cart-api -> dispatches a
 * bubbling `product:added`, so the colleague-owned cart drawer opens on its own)
 * and reflects an "in cart" state. Native Shopify Bundles expand the bundle into
 * its components at cart/checkout; this element only does the PDP presentation +
 * add, it does NOT attach the components itself.
 *
 * Colour sync: the bundle is a real product with its own Color option (e.g.
 * Gold/Silver). When the main product's colour changes (pdp:variant-update on
 * <main-product>), we pick the bundle variant whose Color option value matches the
 * selected colour name and update the add id, button price/compare, availability,
 * and the in-cart check. A `combo:variant-changed` event is dispatched so the
 * sticky ATC can mirror it. Sync is skipped for single-variant bundles (nothing to
 * switch). Matching is by option value name (native bundle options can't be
 * metaobject-connected) and is position-independent on the main product.
 *
 * Cart "in cart" state is read from /cart.js on load and on every `product:added`.
 *
 * Loaded from sections/main-product.liquid.
 */

import { AddToCart } from '@theme/cart-api';

class ComboUpsell extends HTMLElement {
  #bundleVariantId = null;
  #addToCart = null;
  #abortController = null;
  #host = null;
  #bundleVariants = [];
  #idInput = null;
  #button = null;
  #priceEl = null;
  #compareEl = null;
  #cards = [];
  #normalStyle = null;
  #inCartStyle = null;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#bundleVariantId = this.dataset.bundleVariantId || null;
    this.#bundleVariants = this.#parseBundleVariants();
    this.#idInput = this.querySelector('form input[name="id"]');
    this.#button = this.querySelector('form [data-action="add-to-cart"]');
    this.#priceEl = this.querySelector('[data-combo-price]');
    this.#compareEl = this.querySelector('[data-combo-compare]');
    this.#cards = this.#parseCards();
    // Button--* style for the normal vs in-cart state (set as data-* on the button).
    this.#normalStyle = this.#button?.dataset.buttonStyle || null;
    this.#inCartStyle = this.#button?.dataset.inCartButtonStyle || null;

    // Wire the existing [data-action="add-to-cart"] form button to AJAX add.
    if (this.#button) {
      this.#addToCart = new AddToCart(this);
    }

    // Colour sync only matters when the bundle has more than one variant (a real
    // colour choice). Single-variant bundles keep the server-rendered variant.
    if (this.#bundleVariants.length > 1) {
      this.#host = this.closest('main-product');
      this.#host?.addEventListener('pdp:variant-update', this.#onVariantUpdate);
      // The update event only fires on change, so sync the initial colour now.
      const initial = this.#resolveInitialMainVariant();
      if (initial) this.#syncToColour(initial);
    }

    // delegate-exempt: product:added is a document-level custom event (cart-api
    // dispatches it on document); Delegate targets element subtrees, not document.
    document.addEventListener('product:added', this.#onCartChange);
    this.#syncInCart();
  }

  disconnectedCallback() {
    this.#host?.removeEventListener('pdp:variant-update', this.#onVariantUpdate);
    document.removeEventListener('product:added', this.#onCartChange);
    this.#abortController?.abort();
  }

  #onVariantUpdate = (event) => {
    const variant = event.detail?.variant;
    if (variant) this.#syncToColour(variant);
  };

  #onCartChange = () => this.#syncInCart();

  // Pick the bundle variant whose option values are all present in the main
  // variant's selected option values (the colour name). Position-independent:
  // works whether colour is option1/2/3 on the main product. Mixed-colour bundles
  // are out of scope (one Color option after "combine options with matching names").
  #syncToColour(mainVariant) {
    // Compare case/whitespace-insensitively: the main product's option value
    // ("gold") and the native bundle's ("Gold") are entered separately in admin, so
    // their casing can differ for the same colour - an exact compare would wrongly
    // miss and disable the button. Spec is "equals the colour name"; case is not part
    // of the identity.
    const selected = ['option1', 'option2', 'option3']
      .map((key) => mainVariant[key])
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase());
    const match = this.#bundleVariants.find(
      (bv) =>
        bv.options.length > 0 &&
        bv.options.every((o) => selected.includes(String(o).trim().toLowerCase()))
    );

    if (!match) {
      // Selected colour isn't offered as a combo -> can't add it.
      this.#bundleVariantId = null;
      if (this.#idInput) this.#idInput.value = '';
      this.#setAvailable(false);
      this.#emitVariantChanged();
      this.#syncInCart();
      return;
    }

    this.#bundleVariantId = String(match.id);
    this.dataset.bundleVariantId = this.#bundleVariantId;
    if (this.#idInput) this.#idInput.value = this.#bundleVariantId;

    if (this.#priceEl && match.price_formatted) {
      this.#priceEl.textContent = match.price_formatted;
    }
    if (this.#compareEl) {
      const compare = match.compare_at_price_formatted;
      this.#compareEl.textContent = compare || '';
      this.#compareEl.toggleAttribute('hidden', !compare);
    }
    this.#setAvailable(match.available);
    this.#syncCards(match.options);
    this.#emitVariantChanged();
    this.#syncInCart();
  }

  #setAvailable(available) {
    if (this.#button) this.#button.disabled = !available;
  }

  #emitVariantChanged() {
    this.dispatchEvent(
      new CustomEvent('combo:variant-changed', {
        bubbles: true,
        detail: { variantId: this.#bundleVariantId },
      })
    );
  }

  #parseBundleVariants() {
    const node = this.querySelector('[data-combo-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error('combo-upsell: bundle variants JSON parse failed', err);
      return [];
    }
  }

  #parseCards() {
    return Array.from(this.querySelectorAll('[data-combo-card]')).map((element) => {
      const node = element.querySelector('[data-combo-card-variants]');
      let variants = [];
      try {
        variants = node ? JSON.parse(node.textContent) : [];
      } catch (err) {
        console.error('combo-upsell: component variants JSON parse failed', err);
      }
      return { element, variants };
    });
  }

  // Keep every component card on the same colour as the matched bundle variant.
  // Component products may have extra options (for example size), so all bundle
  // option values must be present in the component variant, but additional values
  // are allowed.
  #syncCards(bundleOptions) {
    const wanted = bundleOptions.map((value) => String(value).trim().toLowerCase());

    this.#cards.forEach(({ element, variants }) => {
      const matchesColour = (variant) => {
        const options = variant.options.map((value) => String(value).trim().toLowerCase());
        return wanted.every((value) => options.includes(value));
      };
      const match =
        variants.find((variant) => matchesColour(variant) && variant.available) ||
        variants.find(matchesColour);
      if (!match) return;

      element.querySelectorAll('[data-combo-card-link]').forEach((link) => {
        if (match.url) link.href = match.url;
      });

      const image = element.querySelector('[data-combo-card-image]');
      if (image && match.image_src) {
        image.src = match.image_src;
        image.removeAttribute('srcset');
      }

      const subtitle = element.querySelector('[data-combo-card-subtitle]');
      if (subtitle) {
        const text = match.short_description ?? '';
        subtitle.textContent = text;
        subtitle.toggleAttribute('hidden', text.trim() === '');
      }

      const price = element.querySelector('[data-combo-card-price]');
      if (price && match.price_formatted) price.textContent = match.price_formatted;
    });
  }

  // Resolve the main product's selected variant without depending on <main-product>
  // being upgraded yet (block scripts can run before the section script).
  #resolveInitialMainVariant() {
    const host = this.#host || this.closest('main-product');
    if (host?.currentVariant) return host.currentVariant;
    const node = host?.querySelector('[data-variants-data]');
    if (!node) return null;
    try {
      const variants = JSON.parse(node.textContent);
      const urlId = parseInt(new URL(window.location.href).searchParams.get('variant'), 10);
      if (urlId) {
        const match = variants.find((v) => v.id === urlId);
        if (match) return match;
      }
      return variants.find((v) => v.available) || variants[0] || null;
    } catch {
      return null;
    }
  }

  // Swap the button's Button--* class between the normal and in-cart styles so the
  // button inherits the chosen style's real padding/border/colours (not a CSS hack).
  #applyButtonStyle(inCart) {
    if (!this.#button || !this.#normalStyle || !this.#inCartStyle) return;
    this.#button.classList.remove(`Button--${this.#normalStyle}`, `Button--${this.#inCartStyle}`);
    this.#button.classList.add(`Button--${inCart ? this.#inCartStyle : this.#normalStyle}`);
  }

  async #syncInCart() {
    if (!this.#bundleVariantId) {
      this.toggleAttribute('data-in-cart', false);
      this.#applyButtonStyle(false);
      return;
    }
    // Abort a prior in-flight fetch so rapid product:added / colour changes don't
    // race (slower stale response overwriting fresher state).
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        signal: this.#abortController.signal,
      });
      if (!res.ok) return;
      const cart = await res.json();
      const inCart = (cart.items || []).some(
        (item) => String(item.variant_id) === String(this.#bundleVariantId)
      );
      this.toggleAttribute('data-in-cart', inCart);
      this.#applyButtonStyle(inCart);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('combo-upsell: cart fetch failed', err);
    }
  }
}

if (!customElements.get('combo-upsell')) {
  customElements.define('combo-upsell', ComboUpsell);
}
